import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Save, RotateCcw, Banknote, Coins, Calculator } from 'lucide-react';
import { CashCount, Currency, CashCountDetail } from '../../types';
import { saveCashCount } from '../../services/cashCountService';
import { useFirebase } from '../FirebaseProvider';

interface Denomination {
  value: number;
  label: string;
  type: 'BILL' | 'COIN';
}

const DENOMINATIONS: Record<Currency, Denomination[]> = {
  PYG: [
    { value: 100000, label: '100.000', type: 'BILL' },
    { value: 50000, label: '50.000', type: 'BILL' },
    { value: 20000, label: '20.000', type: 'BILL' },
    { value: 10000, label: '10.000', type: 'BILL' },
    { value: 5000, label: '5.000', type: 'BILL' },
    { value: 2000, label: '2.000', type: 'BILL' },
    { value: 1000, label: '1.000', type: 'COIN' },
    { value: 500, label: '500', type: 'COIN' },
    { value: 100, label: '100', type: 'COIN' },
    { value: 50, label: '50', type: 'COIN' },
  ],
  USD: [
    { value: 100, label: '100', type: 'BILL' },
    { value: 50, label: '50', type: 'BILL' },
    { value: 20, label: '20', type: 'BILL' },
    { value: 10, label: '10', type: 'BILL' },
    { value: 5, label: '5', type: 'BILL' },
    { value: 2, label: '2', type: 'BILL' },
    { value: 1, label: '1', type: 'BILL' },
    { value: 1, label: '1', type: 'COIN' },
    { value: 0.50, label: '0.50', type: 'COIN' },
    { value: 0.25, label: '0.25', type: 'COIN' },
    { value: 0.10, label: '0.10', type: 'COIN' },
    { value: 0.05, label: '0.05', type: 'COIN' },
    { value: 0.01, label: '0.01', type: 'COIN' },
  ],
  ARS: [
    { value: 20000, label: '20.000', type: 'BILL' },
    { value: 10000, label: '10.000', type: 'BILL' },
    { value: 2000, label: '2.000', type: 'BILL' },
    { value: 1000, label: '1.000', type: 'BILL' },
    { value: 500, label: '500', type: 'BILL' },
    { value: 200, label: '200', type: 'BILL' },
    { value: 100, label: '100', type: 'BILL' },
    { value: 50, label: '50', type: 'BILL' },
    { value: 20, label: '20', type: 'BILL' },
    { value: 10, label: '10', type: 'BILL' },
    { value: 10, label: '10', type: 'COIN' },
    { value: 5, label: '5', type: 'COIN' },
    { value: 2, label: '2', type: 'COIN' },
    { value: 1, label: '1', type: 'COIN' },
  ],
  BRL: [
    { value: 200, label: '200', type: 'BILL' },
    { value: 100, label: '100', type: 'BILL' },
    { value: 50, label: '50', type: 'BILL' },
    { value: 20, label: '20', type: 'BILL' },
    { value: 10, label: '10', type: 'BILL' },
    { value: 5, label: '5', type: 'BILL' },
    { value: 2, label: '2', type: 'BILL' },
    { value: 1, label: '1', type: 'COIN' },
    { value: 0.50, label: '0.50', type: 'COIN' },
    { value: 0.25, label: '0.25', type: 'COIN' },
    { value: 0.10, label: '0.10', type: 'COIN' },
    { value: 0.05, label: '0.05', type: 'COIN' },
  ],
};

interface CashCountToolProps {
  onClose: () => void;
}

export const CashCountTool: React.FC<CashCountToolProps> = ({ onClose }) => {
  const { effectiveUserId } = useFirebase();
  const [currency, setCurrency] = useState<Currency>('PYG');
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [isSaving, setIsSaving] = useState(false);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const denominations = DENOMINATIONS[currency];
  const bills = denominations.filter(d => d.type === 'BILL');
  const coins = denominations.filter(d => d.type === 'COIN');

  const handleQuantityChange = (value: number, quantity: string) => {
    const q = parseInt(quantity) || 0;
    setQuantities(prev => ({ ...prev, [value]: q }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number, totalItems: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = (index + 1) % totalItems;
      const nextId = `${currency}-${nextIndex}`;
      inputRefs.current[nextId]?.focus();
      inputRefs.current[nextId]?.select();
    }
  };

  const resetCount = () => {
    setQuantities({});
  };

  const calculateTotal = () => {
    return denominations.reduce((acc, d) => {
      return acc + (quantities[d.value] || 0) * d.value;
    }, 0);
  };

  const handleSave = async () => {
    if (!effectiveUserId) return;
    
    const totalAmount = calculateTotal();
    if (totalAmount <= 0) {
      alert('El total debe ser mayor a cero para guardar.');
      return;
    }

    setIsSaving(true);
    try {
      const details: CashCountDetail[] = denominations
        .filter(d => (quantities[d.value] || 0) > 0)
        .map(d => ({
          denomination: d.value,
          quantity: quantities[d.value] || 0,
          subtotal: (quantities[d.value] || 0) * d.value
        }));

      await saveCashCount(effectiveUserId, {
        userId: effectiveUserId,
        date: new Date().toISOString(),
        currency,
        totalAmount,
        details
      });

      alert('Arqueo de caja guardado exitosamente.');
      resetCount();
    } catch (error) {
      console.error('Error saving cash count:', error);
      alert('Hubo un error al guardar el arqueo.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatValue = (val: number) => {
    return new Intl.NumberFormat(currency === 'PYG' ? 'es-PY' : 'en-US', {
      minimumFractionDigits: currency === 'PYG' ? 0 : 2,
      maximumFractionDigits: currency === 'PYG' ? 0 : 2,
    }).format(val);
  };

  useEffect(() => {
    resetCount();
  }, [currency]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4 overflow-y-auto"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900 z-10 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-500/10 rounded-xl">
              <Calculator className="text-emerald-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Arqueo de Caja</h2>
              <p className="text-sm text-zinc-500">Conteo físico de efectivo</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors text-zinc-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Currency Tabs */}
        <div className="px-6 py-4 bg-zinc-950/30 flex items-center gap-3 overflow-x-auto border-b border-zinc-800 no-scrollbar">
          {(['PYG', 'USD', 'ARS', 'BRL'] as Currency[]).map((cur) => (
            <button
              key={cur}
              onClick={() => setCurrency(cur)}
              className={`flex-1 min-w-[100px] h-14 rounded-xl font-black tracking-widest transition-all whitespace-nowrap border-2 flex items-center justify-center ${
                currency === cur 
                  ? 'bg-emerald-500 border-emerald-400 text-black shadow-xl shadow-emerald-500/20 scale-[1.02]' 
                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
              }`}
            >
              {cur}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bills Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-zinc-400 font-bold px-2 uppercase tracking-widest text-xs">
                <Banknote size={16} />
                Billetes
              </div>
              <div className="space-y-2">
                {bills.map((den, idx) => (
                  <DenominationRow 
                    key={`${currency}-bill-${den.value}`}
                    denomination={den}
                    quantity={quantities[den.value] || 0}
                    onChange={(q) => handleQuantityChange(den.value, q)}
                    onKeyDown={(e) => handleKeyDown(e, idx, denominations.length)}
                    id={`${currency}-${idx}`}
                    inputRef={(el) => inputRefs.current[`${currency}-${idx}`] = el}
                    formatValue={formatValue}
                  />
                ))}
              </div>
            </section>

            {/* Coins Section */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-zinc-400 font-bold px-2 uppercase tracking-widest text-xs">
                <Coins size={16} />
                Monedas
              </div>
              <div className="space-y-2">
                {coins.map((den, idx) => {
                  const actualIdx = idx + bills.length;
                  return (
                    <DenominationRow 
                      key={`${currency}-coin-${den.value}`}
                      denomination={den}
                      quantity={quantities[den.value] || 0}
                      onChange={(q) => handleQuantityChange(den.value, q)}
                      onKeyDown={(e) => handleKeyDown(e, actualIdx, denominations.length)}
                      id={`${currency}-${actualIdx}`}
                      inputRef={(el) => inputRefs.current[`${currency}-${actualIdx}`] = el}
                      formatValue={formatValue}
                    />
                  );
                })}
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky bottom-0 rounded-b-2xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col">
              <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Total Contado ({currency})</span>
              <span className="text-3xl font-black text-emerald-500 font-mono tracking-tight">
                {currency === 'PYG' ? '' : currency + ' '}
                {new Intl.NumberFormat(currency === 'PYG' ? 'es-PY' : 'en-US', {
                  minimumFractionDigits: currency === 'PYG' ? 0 : 2,
                  style: currency === 'PYG' ? 'currency' : 'decimal',
                  currency: 'PYG'
                }).format(calculateTotal())}
              </span>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={resetCount}
                className="flex-1 md:flex-none px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-bold transition-all flex items-center justify-center gap-2 border border-zinc-700"
              >
                <RotateCcw size={20} />
                Limpiar
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 md:flex-none px-8 py-3 bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                ) : (
                  <Save size={20} />
                )}
                Guardar Arqueo
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

interface DenominationRowProps {
  denomination: Denomination;
  quantity: number;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  id: string;
  inputRef: (el: HTMLInputElement | null) => void;
  formatValue: (val: number) => string;
}

const DenominationRow: React.FC<DenominationRowProps> = ({ 
  denomination, 
  quantity, 
  onChange, 
  onKeyDown, 
  id, 
  inputRef,
  formatValue
}) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-zinc-700 transition-all group">
      <div className="w-24 text-sm font-bold text-zinc-300">
        {denomination.label}
      </div>
      <div className="flex-1">
        <input
          ref={inputRef}
          id={id}
          type="number"
          inputMode="numeric"
          value={quantity || ''}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="0"
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-center text-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 group-hover:border-zinc-600 transition-all"
        />
      </div>
      <div className="w-32 text-right text-sm font-medium text-zinc-500 group-focus-within:text-emerald-400 transition-colors">
        {formatValue(quantity * denomination.value)}
      </div>
    </div>
  );
};
